import { loadModules } from 'esri-loader';
import debounce from 'lodash/debounce';
import moment from 'moment';
import originFields from '../../defaults/PointOfOriginFields.json'
import uncertifiedPerimeterFields from '../../defaults/UncertifiedPerimeterFields.json'
import { getSQLDate, getSQLDateEndOfDay } from '../../utils/incidentHelper';
import consts from '../../defaults/Enums.json'
import symbols from '../../defaults/Symbols.json'
import { createLegend } from '../../utils/esriLoader';
import { getAcresFieldName } from '../../utils/incidentHelper';
import Graphic from "@arcgis/core/Graphic.js";

export default class LayerManager {

  constructor(view, config, search, isReadOnly) {

    //TODO: Resolve this.capabilityRequestIrwinIds = [];
    //TODO: Resolve this.perimeterFilterIrwinIds = [];
    this.shouldFilterOnPerimeters = false;
    this.perimeterMapMethod = null;
    this.perimeterFeatureStatus = null;
    this.perimeterFeatureAccess = null;
    this.capabilityRequestOption = null;
    this.spatialFilterPolygon = null;
    this.maxCountForIncidents = 1000;
    this.searchWidget = search;

    return new Promise((resolve, reject) => {
      this.view = view;
      this.map = view.map;
      this.config = config;
      this.isReadOnly = isReadOnly;

      this.initLayers()
        .then(
          result => {
            createLegend(this.view, this.originFeatureLayer, this.viirsFeatureLayer);
            window.api.setOriginApiUrl(this.config.originApiUrl[this.config.env]);
            this.initEventHandlers();
            this.initSearch(search);

            if (this.jurisdictionalFeatureLayer) 
            {
              window.api.setJurisdictionalFeatureLayerUrl(this.jurisdictionalFeatureLayer.url + '/0');
              window.api.setJurisdictionalFeatureLayerToken(this.jurisdictionalFeatureLayer.portalItem.portal.credential.token)
            }
            resolve();
          },
          error => {
            reject();
          }
        );
    });
  }

  initSearch(searchWidget) {
    loadModules(['esri/tasks/Locator', 'esri/layers/FeatureLayer'])
      .then(([Locator, FeatureLayer]) => {
        searchWidget.sources.removeAll();

        searchWidget.sources.add({
          layer: new FeatureLayer({
            url: this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId,
            outFields: ["OBJECTID", "IncidentName", "UniqueFireIdentifier", "FORID"]
          }),
          searchFields: this.config.originLayer.search.fields,
          exactMatch: false,
          outFields: ["OBJECTID", "IncidentName", "UniqueFireIdentifier", "FORID"],
          name: "Incidents",
          maxSuggestions: 10,
          suggestionsEnabled: true,
          suggestionTemplate: "Name: {IncidentName} \nID: {UniqueFireIdentifier}",
          minSuggestCharacters: 0,
          withinViewEnabled: false,
          resultGraphicEnabled: false,
          popupTemplate: this.originPopupTemplate
        },0);

        searchWidget.sources.add({
          locator: new Locator({
            url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer",  //TODO: Fix URL hardcode
            name: "US Locator",
            countryCode: 'US'
          }),
          singleLineFieldName: "SingleLine",
          outFields: ["Addr_type"],
          name: "US Locator",
          countryCode: 'US',
          localSearchOptions: {
            minScale: 300000,
            distance: 50000
          },
          maxResults: 3,
          maxSuggestions: 3
        }, 1);
        searchWidget.activeSourceIndex = -1;
      });
  }


  initEventHandlers() {
    loadModules(['dojo/topic'])
      .then(([topic]) => {

        //GET
        topic.subscribe('LayerManager/get-origin-definition-expression', this.handleGetOriginDefinitionExpression.bind(this));
        topic.subscribe('LayerManager/get-origin-fields', this.handleGetOriginFields.bind(this));
        topic.subscribe('LayerManager/get-origin-features', this.handleGetOriginFeatures.bind(this));
        topic.subscribe('LayerManager/get-full-origin-features-from-api', this.handleGetFullOriginFeaturesFromApi.bind(this));
        topic.subscribe('LayerManager/get-origin-feature', this.handleGetOriginFeature.bind(this));
        topic.subscribe('LayerManager/set-origin-selection-graphics', this.handleSetOriginSelectionGraphics.bind(this));
        topic.subscribe('LayerManager/set-origin-selection-graphic', this.handleSetOriginSelectionGraphic.bind(this));
        topic.subscribe('LayerManager/set-origin-definition-expression', debounce(this.handleSetOriginDefinitionExpression.bind(this), 500));
        topic.subscribe('LayerManager/set-custom-filter-definition-expression', this.handleSetCustomFilterDefinitionExpression.bind(this));
        topic.subscribe('LayerManager/set-visible-perimeters', this.handleSetVisiblePerimeters.bind(this));
        topic.subscribe('LayerManager/get-forids-for-query', this.handleGetFORIDsForQuery.bind(this));
        topic.subscribe('LayerManager/set-perimeter-graphic', this.handleSetPerimeterGraphic.bind(this));       
        topic.subscribe('LayerManager/clear-perimeter-graphics', this.handleClearPerimeterGraphics.bind(this));   
        topic.subscribe('LayerManager/set-modified-perimeter', this.handleSetModifiedPerimeter.bind(this));
        topic.subscribe('LayerManager/set-modified-perimeter-no-clear', this.handleSetModifiedPerimeterNoClear.bind(this));
        topic.subscribe('LayerManager/add-debug-graphic', this.handleAddDebugGraphic.bind(this));
        topic.subscribe('LayerManager/set-perimeters-symbology', this.handleSetPerimetersSymbol.bind(this));
        topic.subscribe('LayerManager/set-origin-visible', this.handleSetOriginVisible.bind(this));
        topic.subscribe('LayerManager/clear-visible-perimeters', this.handleClearVisiblePerimeters.bind(this));
        topic.subscribe('LayerManager/clear-origin-selection-graphics', this.handleClearOriginSelectionGraphics.bind(this));
        topic.subscribe('LayerManager/clear-modified-perimeter', this.handleClearModifiedPerimeter.bind(this));
        topic.subscribe('LayerManager/origin-refresh', this.handleOriginRefresh.bind(this));
        topic.subscribe('LayerManager/is-origin-within-perimeter', this.handleOriginWithinPerimeter.bind(this));
        topic.subscribe('LayerManager/on-update-end-origin-layer', this.handleOriginUpdateEnd.bind(this));
        topic.subscribe('LayerManager/set-spatial-filter-polygon', this.handleSetSpatialFilterPolygon.bind(this));
      });
  }

  initLayers() {
    return new Promise((resolve, reject) => {
      loadModules([
        'esri/geometry/Polygon',
        'esri/geometry/SpatialReference',
        'esri/geometry/support/webMercatorUtils',
        'esri/layers/Layer',
        'esri/layers/FeatureLayer',
        'esri/layers/TileLayer',
        'esri/layers/GraphicsLayer',
        'esri/symbols/SimpleMarkerSymbol',
        'esri/core/watchUtils'
      ])
        .then((
          [
            Polygon,
            SpatialReference,
            WebMercatorUtils,
            Layer,
            FeatureLayer,
            TileLayer,
            GraphicsLayer,
            SimpleMarkerSymbol,
            watchUtils
          ]
        ) => {

          this.heatmapRenderer = {
            type: "heatmap",
            colorStops: [
              { ratio: 0, color: "rgba(255, 255, 255, 0)" },
              { ratio: 0.2, color: "rgba(255, 255, 255, 1)" },
              { ratio: 0.5, color: "rgba(255, 140, 0, 1)" },
              { ratio: 0.8, color: "rgba(255, 140, 0, 1)" },
              { ratio: 1, color: "rgba(255, 0, 0, 1)" }
            ],
            maxPixelIntensity: 500,
            minPixelIntensity: 100
          };

          this.originPopupTemplate = {
            title: `Incident Name: {${originFields.name}}`,
            alignment: "bottom-center",
            actions: [
              {
                title: "View Incident",
                id: "popup-view-incident",
                className: "esri-icon-review"
              },
              {
                title: "Zoom To",
                id: "popup-zoom-to-incident",
                className: "esri-icon-zoom-in-magnifying-glass"
              }
            ],
            overwriteActions: true,
            fieldInfos: [
              {
                fieldName: "FORID",
                label: "FORID"
              },
              {
                fieldName: "IncidentSize",
                label: "Fire Size",
              },
              {
                fieldName: "FireCode",
                label: "Fire Code"
              },
              {
                fieldName: "FireDiscoveryDateTime",
                label: "Discovery",
                /*format:
                {
                  dateFormat: "short-date"
                }*/
              },
              {
                fieldName: "FireOutDateTime",
                label: "Fire Out",
                /*format:
                {
                  dateFormat: "short-date"
                }*/
              } 
            ],
            expressionInfos:
              [
                {
                  name: "arcade-for-id",
                  expression: "DefaultValue($feature.FORID, '--')"
                },
                {
                  name: "arcade-incident-size",
                  expression: "DefaultValue($feature.IncidentSize, '--')"
                },
                {
                  name: "arcade-fire-code",
                  expression: "DefaultValue($feature.FireCode, '--')"
                },
                {
                  name: "arcade-discovery-date",
                  expression: "When(IsEmpty($feature.FireDiscoveryDateTime), '--', Text(Date($feature.FireDiscoveryDateTime), 'MM/DD/YYYY'))"
                },
                {
                  name: "arcade-fire-out-date",
                  expression: "When(IsEmpty($feature.FireOutDateTime), '--', Text(Date($feature.FireOutDateTime), 'MM/DD/YYYY'))"
                }
                /*{
                  name: "arcade-discovery-date",
                  expression: "When(IsEmpty($feature.FireDiscoveryDateTime), '--', Text($feature.FireDiscoveryDateTime, 'MM/DD/YYYY'))"
                },
                {
                  name: "arcade-fire-out-date",
                  expression: "When(IsEmpty($feature.FireOutDateTime), '--', Text($feature.FireOutDateTime, 'MM/DD/YYYY'))"
                }*/
              ],
            content: [
              {
                type: "text",
                text:
                  '<div class="popup-incident-primary-fields">' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Incident Name</label>' +
                  '<p class="list-item-incident-name">{IncidentName}</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>ID</label>' +
                  '<p class="list-item-detail-label">{UniqueFireIdentifier}</p></div></div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Fire Size</label>' +
                  '<p>{expression/arcade-incident-size}</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>FireCode</label>' +
                  '<p>{expression/arcade-fire-code}</p>' +
                  '</div>' +
                  '</div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Discovery</label>' +
                  '<p>{expression/arcade-discovery-date}</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>Fire Out</label>' +
                  '<p>{expression/arcade-fire-out-date}</p>' +
                  '</div>' +
                  '</div>' +
                  '</div>'
              }
            ]
          };

          this.pointRenderer = {
            type: "unique-value",
            valueExpression: `
                   var nonWildfire = ($feature.IncidentTypeCategory != 'RX' && $feature.IncidentTypeCategory != 'WF');
                   var isComplete = ($feature.StatusID == 1);
                   var isCertified = ($feature.StatusID == 3);
                   var nonWildFireIncomplete = (nonWildfire && !isComplete && !isCertified);
                   var nonWildFireComplete = (nonWildfire && isComplete && !isCertified);
                   var isQuarantined = ($feature.IsQuarantined == 1);
                   var prescribed = ($feature.IncidentTypeCategory == 'RX');
                   var notUnique = (!IsEmpty($feature.MinUniqueness) && ($feature.MinUniqueness <= .25 && !isCertified));
                   var notUniqueAndPrescribed = (prescribed && notUnique && !isCertified);
                   var nonWildFireNonUnique = (nonWildFireIncomplete && notUnique && !isCertified));
                  return When($feature.IsQuarantined == 1, 'IsQuarantined', notUniqueAndPrescribed, 'NonUniquePrescribed', notUnique, 'NonUnique', prescribed, 'PrescribedFire', isCertified, 'Certified', nonWildFireIncomplete, 'NonWildfire', nonWildFireComplete, 'NonWildfireComplete', isComplete, 'Complete', 'Incomplete')`,
            uniqueValueInfos: [
              {
                value: 'Certified',
                label: "Certified",
                symbol: symbols.certifiedSymbol
              },
              {
                value: consts.incidentStatus.complete,
                label: "Complete",
                symbol: symbols.completeSymbol
              },
              {
                value: consts.incidentStatus.incomplete,
                label: "Incomplete",
                symbol: symbols.defaultIncidentSymbol
              },
              {
                value: 'IsQuarantined',
                label: "Is Quarantined",
                symbol: symbols.conflictIncidentSymbol
              },
              {
                value: 'NonUnique',
                label: "Non Unique",
                symbol: symbols.nonUniqueSymbol
              },
              {
                value: 'NonUniquePrescribed',
                label: "Non Unique - Prescribed",
                symbol: symbols.prescribedFireNonUniqueSymbol
              },
              {
                value: 'NonUniqueNonWildFire',
                label: "Non Unique - Other",
                symbol: symbols.otherFireNonUniqueSymbol
              },
              {
                value: 'PrescribedFire',
                label: "Prescribed Fire",
                symbol: symbols.prescribedFireSymbol
              },
              {
                value: 'NonWildfire',
                label: "Other Fire Type",
                symbol: symbols.otherFireSymbol
              },
              {
                value: 'NonWildfireComplete',
                label: "Other Fire Type - Complete",
                symbol: symbols.otherFireSymbolComplete
              }
            ],
            visualVariables: [{
              type: "size",
              field: "IncidentSize",
              stops: [
                { value: 0.25, size: 10, label: "Class A" },
                { value: 10, size: 14, label: "Class B" },
                { value: 100, size: 18, label: "Class C" },
                { value: 300, size: 22, label: "Class D" },
                { value: 1000, size: 26, label: "Class E" },
                { value: 5000, size: 30, label: "Class F" },
                { value: 50000, size: 34, label: "Class G" }
              ]
            }]
          };

          let originConfig = {
            portalItem: { id: this.config.originLayer.portalItemId },
            definitionExpression: this.config.originLayer.defaultWhere,
            outFields: [...new Set([...originFields.requiredOutFields])],
            visible: true,
            recordCount: 20,
            //renderer: this.pointRenderer,
            popupTemplate: this.originPopupTemplate
          };


          this.fodrGraphicsLayer = new GraphicsLayer();
          this.fodrGraphicsLayer.renderer = this.pointRenderer;
          this.fodrGraphicsLayer.visible = true;
          this.fodrGraphicsLayer.listMode = "hide";

          this.originFeatureLayer = new FeatureLayer(Object.assign({}, originConfig, this.config.originLayer.jsInit[this.config.env]));
          this.originFeatureLayer.listMode = "hide";
          this.originFeatureLayer.visible = true;
          this.originFeatureLayer.renderer = this.pointRenderer;

          this.jurisdictionalRenderer = {
            type: "unique-value",
            field: "LegendJurisdictionalAgency",  //TODO: Fix hardcode
            uniqueValueInfos: [
              {
                value: "ANSCA",
                label: "ANSCA",
                symbol: symbols.jurisdictionalLayer.TribalSymbol
              },
              {
                value: "BIA",
                label: "BIA",
                symbol: symbols.jurisdictionalLayer.TribalSymbol
              },
              {
                value: "Tribal",
                label: "Tribal",
                symbol: symbols.jurisdictionalLayer.TribalSymbol
              },
              {
                value: "BLM",
                label: "Bureau of Land Management (BLM)",
                symbol: symbols.jurisdictionalLayer.BLMSymbol
              },
              {
                value: "BOR",
                label: "Bureau of Reclamation",
                symbol: symbols.jurisdictionalLayer.BORSymbol
              },
              {
                value: "DOD",
                label: "Military Reservations and Corps of Engineer",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              },
              {
                value: "DOE",
                label: "Military Reservations and Corps of Engineer",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              },
              {
                value: "NPS",
                label: "National Park Service (NPS)",
                symbol: symbols.jurisdictionalLayer.NPSSymbol
              },
              {
                value: "OtherFed",
                label: "OtherFederal",
                symbol: symbols.jurisdictionalLayer.OtherFedSymbol
              },
              {
                value: "State",
                label: "State",
                symbol: symbols.jurisdictionalLayer.StateSymbol
              },
              {
                value: "CNTY",
                label: "City/County",
                symbol: symbols.jurisdictionalLayer.CityCountySymbol
              },
              {
                value: "Private",
                label: "Private",
                symbol: symbols.jurisdictionalLayer.PrivateSymbol
              },
              {
                value: "USACOE",
                label: "Corps of Engineer",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              },
              {
                value: "USFS",
                label: "US Forest Service (USFS)",
                symbol: symbols.jurisdictionalLayer.ForestServiceSymbol
              },
              {
                value: "USFWS",
                label: "US Fish and Wildlife (USFW) National Wildlife Refuge",
                symbol: symbols.jurisdictionalLayer.USFWSSymbol
              },
              {
                value: "USGS",
                label: "USGS",
                symbol: symbols.jurisdictionalLayer.OtherFedSymbol
              },
              {
                value: "USCG",
                label: "USCG",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              }
            ]
          };

          let jurisdictionalFeatureConfig = {
            portalItem: { id: this.config.jurisdictionalFeatureLayer.portalItemId },
            title: "Jurisdictional Units",
            outFields: ["*"],  //TODO: Fix hardcode
            visible: false,
            popupTemplate: { // autocasts as new PopupTemplate()
              title: "{JurisdictionalUnitName}",
              content: "<div class=\"popup-incident-primary-fields\">Landowner Kind: {LandownerKind}</br>Landowner Category: {LandownerCategory}</br>Unit ID: {JurisdictionalUnitID_sansUS}</br>Unit Name: {JurisdictionalUnitName}</br></div>",
              overwriteActions: true
            },
            opacity: .75,
            labelsVisible: true,
            legendEnabled: false,
            renderer: this.jurisdictionalRenderer
          };


          const perimeterConfig = {
            visible: false,
            title: "NIFS Perimeters",   //TODO: Fix layername hardcode
            //definitionExpression: "1=2",
            outFields: ["*"],
            renderer: {
              type: "simple",  // autocasts as new SimpleRenderer()
              symbol: symbols.selectedPerimeterSymbol
            }
          };

          const fireHistoryConfig = {
            portalItem: this.config.fireHistoryLayer.jsInit[this.config.env].portalItem,
            visible: false,
            title: "Fire History Perimeters",
            opacity: 0.5,
            outFields: ["IncidentName", "FeatureCategory", "MapMethod", "Comments", "GISAcres", "DeleteThis", "FeatureAccess", "FeatureStatus", "IRWINID", "Source"],
            renderer: {
              type: "simple",
              symbol: symbols.selectedPerimeterSymbol
            }
          };

          const finalFireConfig = {
            portalItem: this.config.finalFireFeatureLayer.jsInit[this.config.env].portalItem,
            visible: false,
            title: "Final Fire Perimeters", 
            opacity: 0.5,  
            outFields: ["IncidentName","FeatureCategory","MapMethod","GISAcres","FeatureStatus","IRWINID"],
            renderer: {
              type: "simple", 
              symbol: symbols.selectedPerimeterSymbol
            }
          };
          this.jurisdictionalFeatureLayer = new FeatureLayer(Object.assign({}, jurisdictionalFeatureConfig, this.config.jurisdictionalFeatureLayer));
          this.jurisdictionalFeatureLayer.listMode = "hide";
          this.jurisdictionalFeatureLayer.title = consts.layerTitles.jurisdictionalUnitsFeatureLayer;
          //TODO: Fix layername hardcode
          this.gaccFeatureLayer = new FeatureLayer(
            {
              url: 'https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/National_GACC_Current/FeatureServer',  //TODO: Fix hardcode
              title: "GACC",
              outFields: ["GACCName", "GACCUnitID", "GACCAbbreviation", "ContactPhone"],  //TODO: Fix hardcode
              popupTemplate: { // autocasts as new PopupTemplate()
                title: "{GACCName}",
                content: "<div class=\"popup-incident-primary-fields\">Name: {GACCName}</br>Unit ID: {GACCUnitID}</br>Abbreviation: {GACCAbbreviation}</br>Contact Phone: {ContactPhone}</div>",
                overwriteActions: true
              },
              popupEnabled: true,
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              renderer: {
                type: "simple",
                symbol: symbols.gaccSymbol
              }

            });

          this.viirsFeatureLayer = new FeatureLayer(
            {
              url: 'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Satellite_VIIRS_Thermal_Hotspots_and_Fire_Activity/FeatureServer',  //TODO: Fix hardcode
              title: "VIIRS",
              outFields: ["OBJECTID",
                "satellite",
                "esritimeutc",
                "latitude",
                "longitude",
                "FRP",
                "confidence",
                "hours_old"],
              visible: false,
              popupEnabled: true,
              popupTemplate: {
                title: "VIIRS",
                expressionInfos:
                  [
                    {
                      name: "arcade-for-satellite",
                      title: "Satellite",
                      expression: "When($feature.satellite == 'N', 'Suomi NPP', $feature.satellite == '1', 'NOAA-20', $feature.satellite)"
                    }
                  ],
                content: [
                  {
                    type: "fields",
                    fieldInfos: [
                      {
                        fieldName: "expression/arcade-for-satellite"
                      },
                      {
                        fieldName: "esritimeutc",
                        label: "Time (UTC)"
                      },
                      {
                        fieldName: "latitude",
                        label: "Latitude"
                      },
                      {
                        fieldName: "longitude",
                        label: "Longitude"
                      },
                      {
                        fieldName: "FRP",
                        label: "Fire Radiative Power"
                      },
                      {
                        fieldName: "confidence",
                        label: "Confidence"
                      },
                      {
                        fieldName: "hours_old",
                        label: "Hours Old"
                      },
                    ]
                  }
                ]
              },
              opacity: 0.6,
              labelsVisible: true,
              legendEnabled: true,
              renderer: {
                type: "class-breaks",
                field: "hours_old",
                classBreakInfos: [
                  {
                    minValue: 48,
                    maxValue: 168,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#FFFF00",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 24,
                    maxValue: 48,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#FFA900",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 12,
                    maxValue: 24,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#FF5500",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 6,
                    maxValue: 12,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#E64D00",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 0,
                    maxValue: 6,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#A80000",
                      size: "10px"
                    }
                  }
                ]
              }
            });

          const stateLabelClass = {
            // autocasts as new LabelClass()
            symbol: {
              type: "text",  // autocasts as new TextSymbol()
              color: "black",
              font: {  // autocast as new Font()
                family: "Avenir Next",
                size: 12,
                weight: "bold"
              }
            },
            labelPlacement: "above-center",
            labelExpressionInfo: {
              expression: "$feature.NAME"
            }
          };


          //TODO: Fix Url hardcode and title
          this.stateFeatureLayer = new FeatureLayer(
            {
              url: 'https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/US_State_and_County/FeatureServer/1',  //TODO: Fix hardcode
              title: "States",
              popupEnabled: true,
              popupTemplate: { // autocasts as new PopupTemplate()
                title: "{NAME}",
                content: "<div class=\"popup-incident-primary-fields\">State: {NAME}</br></div>",
                overwriteActions: true
              },
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              labelingInfo: [stateLabelClass],
              renderer: {
                type: "simple",
                symbol: symbols.gaccSymbol
              }
            });

          const countyLabelClass = {
            // autocasts as new LabelClass()
            symbol: {
              type: "text",  // autocasts as new TextSymbol()
              color: "black",
              font: {  // autocast as new Font()
                family: "Avenir Next",
                size: 12,
                weight: "bold"
              }
            },
            labelPlacement: "above-center",
            labelExpressionInfo: {
              expression: "$feature.NAME"
            }
          };

          //TODO: Fix Url hardcode and title
          this.countyFeatureLayer = new FeatureLayer(
            {
              url: 'https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/US_State_and_County/FeatureServer/2',  //TODO: Fix hardcode
              title: "Counties",
              popupEnabled: true,
              popupTemplate: {
                title: "{NAME}",
                content: "<div class=\"popup-incident-primary-fields\">County: {NAME}</div>",
                overwriteActions: true
              },
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              labelingInfo: [countyLabelClass],
              renderer: {
                type: "simple",
                symbol: symbols.gaccSymbol
              }

            });

          //TODO: Fix Url hardcode and title
          this.fsTopoLayer = new TileLayer(
            {
              url: "https://apps.fs.usda.gov/arcx/rest/services/EDW/EDW_FSTopo_01/MapServer", //TODO: Fix hardcode
              title: "Forest Service Topo",
              visible: false,
              opacity: 0.75,
              legendEnabled: true
            });

          //TODO: Fix Url hardcode and title
          this.dispatchCenterFeatureLayer = new FeatureLayer(
            {
              url: 'https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/National_Dispatch_Current/FeatureServer/0',  //TODO: Fix hardcodes
              title: "National Dispatch Centers",
              outFields: ["OBJECTID", "DispUnitID", "DispName", "GACCAbbreviation"],
              fields: ["OBJECTID", "DispUnitID", "DispName", "GACCAbbreviation"],
              popupTemplate: { // autocasts as new PopupTemplate()
                title: "{DispName}",
                content: "<div class=\"popup-incident-primary-fields\">Name: {DispName}</br>Unit ID: {DispUnitID}</br></div>",
                overwriteActions: true
              },
              popupEnabled: true,
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              renderer: {
                type: "simple",
                symbol: symbols.dispatchCenterSymbol
              }

            });


          this.jurisdictionalTileLayer = new TileLayer({
            url: "https://tiles.arcgis.com/tiles/T4QMspbfLg3qTGWY/arcgis/rest/services/Jurisdictional_Agencies/MapServer",
            title: "Jurisdictional Units",
            visible: false,
            opacity: 1,
            legendEnabled: true
          });
          this.perimeterFeatureLayer = new FeatureLayer(Object.assign({}, perimeterConfig, this.config.perimeterLayer.jsInit[this.config.env]));
          this.perimeterFeatureLayer.listMode = "hide";

          this.fireHistoryLayer = new FeatureLayer(Object.assign({}, fireHistoryConfig, this.config.fireHistoryLayer.jsInit[this.config.env]));
          this.fireHistoryLayer.listMode = "show";
          this.fireHistoryLayer.popupTemplate = {
            title: "{IncidentName}",
            actions: 
              [
                {
                  title: "Edit",
                  id: "popup-add-irwinid-firehistory",
                  className: "esri-icon-edit"
                }],
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "IncidentName",
                    label: "Incident Name"
                  },
                  {
                    fieldName: "FeatureCategory",
                    label: "Category"
                  },
                  {
                    fieldName: "MapMethod",
                    label: "Map Method"
                  },
                  {
                    fieldName: "IRWINID",
                    label: "IRWIN ID"
                  },
                  {
                    fieldName: "GisAcres",
                    label: "Acres"
                  },
                  {
                    fieldName: "PolygonDateTime",
                    label: "Polygon Date Time"
                  },
                  {
                    fieldName: "FeatureStatus",
                    label: "Feature Status"
                  },
                  {
                    fieldName: "Collector",
                    label: "Collector"
                  }
                ]
              }
            ]
          };
         
          this.finalFireLayer = new FeatureLayer(Object.assign({}, finalFireConfig, this.config.finalFireFeatureLayer.jsInit[this.config.env]));
          this.finalFireLayer.listMode = "show";

          this.perimeterGraphicsLayer = new GraphicsLayer();
          this.perimeterGraphicsLayer.listMode = "hide";

          this.originSelectionGraphicsLayer = new GraphicsLayer();
          this.originSelectionGraphicsLayer.listMode = "hide";

          this.perimeterModifiedGraphicsLayer = new GraphicsLayer();
          this.perimeterModifiedGraphicsLayer.listMode = "hide";

          this.spatialFilterGraphicsLayer = new GraphicsLayer();
          this.spatialFilterGraphicsLayer.listMode = "hide";

          this.originSelectionSymbol = new SimpleMarkerSymbol(symbols.selectionSymbol);
          this.originDisabledSymbol = new SimpleMarkerSymbol(symbols.disabledSymbol);

          if (this.gaccFeatureLayer) this.map.add(this.gaccFeatureLayer);
          if (this.dispatchCenterFeatureLayer) this.map.add(this.dispatchCenterFeatureLayer);
          if (this.jurisdictionalFeatureLayer) this.map.add(this.jurisdictionalFeatureLayer);
          
          if (this.jurisdictionalTileLayer) this.map.add(this.jurisdictionalTileLayer);
          if (this.fireHistoryLayer) this.map.add(this.fireHistoryLayer);
          if (this.perimeterFeatureLayer) this.map.add(this.perimeterFeatureLayer);
          if (this.finalFireLayer) this.map.add(this.finalFireLayer);
          if (this.fsTopoLayer) this.map.add(this.fsTopoLayer);
          if (this.viirsFeatureLayer) this.map.add(this.viirsFeatureLayer);
          if (this.countyFeatureLayer) this.map.add(this.countyFeatureLayer);
          if (this.stateFeatureLayer) this.map.add(this.stateFeatureLayer);

          if (this.originFeatureLayer) this.map.add(this.originFeatureLayer);
          
          this.map.add(this.fodrGraphicsLayer);
          this.map.add(this.perimeterGraphicsLayer);
          this.map.add(this.perimeterModifiedGraphicsLayer);
          this.map.add(this.spatialFilterGraphicsLayer);
          this.map.add(this.originSelectionGraphicsLayer);

          //Yoke JurisdictionalFeatureLayer visiblity (empty symbology) to JurisdictionalTileLayer visibility
          watchUtils.watch(this.jurisdictionalTileLayer, 'visible', function () {
            var jurisdictionalUnitsFeatureLayer = this.parent.allLayers.find(function (layer) {
              return layer.title === consts.layerTitles.jurisdictionalUnitsFeatureLayer;
            });

            if (jurisdictionalUnitsFeatureLayer) {
              jurisdictionalUnitsFeatureLayer.visible = this.visible;
            }
          });

          //the universe in lat lon
          var latLonUniverse = new Polygon(new SpatialReference(4269));
          latLonUniverse.rings = [[[-179.9999999, -80.1], [-179.9999999, 80.1], [179.9999999, 80.1], [179.999999, -80.1], [-179.99999999, -80.1]]];

          //the universe in web mercator
          this.universeWebMercator = WebMercatorUtils.geographicToWebMercator(latLonUniverse);

          this.view.whenLayerView(this.originFeatureLayer).then(
            layerview => {
              window.api.setOriginLayerUrl(this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId);
              window.api.setOriginLayerToken(this.originFeatureLayer.portalItem.portal.credential.token);
              this.originLayerView = layerview;
              resolve();
            },
            error => {
              console.log("No Layer view for origin layer")
              reject();
            }
          );


          this.view.watch("scale", (newValue) => {
            if (this.jurisdictionalTileLayer.visible) {
              if (newValue > 72300) {
                this.jurisdictionalFeatureLayer.renderer = {
                  type: "simple",
                  symbol: {
                    "type": "simple-fill",
                    "style": "solid",
                    "color": [0, 0, 0, 0],
                    "outline": {
                      "color": [0, 0, 0, 0],
                      "width": 1
                    }
                  }
                };
                this.jurisdictionalFeatureLayer.refresh();
              }
              else {
                this.jurisdictionalFeatureLayer.renderer = this.jurisdictionalRenderer;
                this.jurisdictionalFeatureLayer.refresh();
              }

            }
            //this.originFeatureLayer.renderer = (parseFloat(newValue) <= 500000) ? this.pointRenderer : this.heatmapRenderer;
            //this.originFeatureLayer.effect = (parseFloat(newValue) >= 500000) ? "bloom(1.3, 0.1px, 15%)" : null;

          });

          //TODO: Change this.
          for (let portalJson of this.config.supportLayers[this.config.env]) {

            Layer.fromPortalItem(portalJson).then(layer => {

              layer.visible = false;
              layer.opacity = 0.5;
              layer.title = "NIFS Perimeters";  //TODO: Fix title hardcode

              this.map.add(layer, 5);

              layer.on("layerview-create", function (event) {
                for (var i = 0; i < event.layerView.layer.layers.items.length; i++) {
                  var layer = event.layerView.layer.layers.items[i];
                  if (layer.layerId !== 6) 
                  {
                    layer.visible = false;
                  }
                  else 
                  {
                    layer.definitionExpression = `DeleteThis IS NULL OR DeleteThis = 'NO' OR DeleteThis = 'No'`
                    // Add this action to the popup so it is always available in this view
                    var template = {
                      // autocasts as new PopupTemplate()
                      title: "{IncidentName}",
                      actions: (this.isReadOnly) ? [] :
                        [
                          {
                            title: "Edit",
                            id: "popup-add-irwinid-nifs",
                            className: "esri-icon-edit"
                          }],
                      //TODO: Fix field hardcodes
                      content: [
                        {
                          type: "fields",
                          fieldInfos: [
                            {
                              fieldName: "IncidentName",
                              label: "Incident Name"
                            },
                            {
                              fieldName: "FeatureCategory",
                              label: "Category"
                            },
                            {
                              fieldName: "MapMethod",
                              label: "Map Method"
                            },
                            {
                              fieldName: "IRWINID",
                              label: "IRWIN ID"
                            },
                            {
                              fieldName: "GisAcres",
                              label: "Acres"
                            },
                            {
                              fieldName: "PolygonDateTime",
                              label: "Polygon Date Time"
                            },
                            {
                              fieldName: "FeatureStatus",
                              label: "Feature Status"
                            },
                            {
                              fieldName: "Collector",
                              label: "Collector"
                            }
                          ]
                        }
                      ]
                    };
                    layer.popupTemplate = template;
                  }
                }
              });
            });
          }
        });
    });


  }

  getSymbolForFeature = (feature, renderValue) => {

    switch (renderValue) 
    {
      case 'Certified':
        return symbols.certifiedSymbol;

      case 'Complete':
        return symbols.completeSymbol;

      case 'Incomplete':
        return symbols.defaultIncidentSymbol;

      case 'IsQuarantined':
        return symbols.conflictIncidentSymbol;

      case 'NonUnique':
        return symbols.nonUniqueSymbol;

      case 'NonUniquePrescribed':
        return symbols.prescribedFireNonUniqueSymbol;

      case 'PrescribedFire':
        return symbols.prescribedFireSymbol;

      case 'NonUniqueNonWildFire':
        return symbols.otherFireNonUniqueSymbol;

      case 'NonWildfire':
        return symbols.otherFireSymbol;

      case 'NonWildfireComplete':
        return symbols.otherFireCompleteSymbol;

      default:
          return symbols.defaultIncidentSymbol;
    }
  }

  getSizeForSymbol = (incidentSize) => {
    if (incidentSize) {
      if (incidentSize <= 0.25) return 10;
      else if (incidentSize <= 10) return 14;
      else if (incidentSize <= 100) return 18;
      else if (incidentSize <= 300) return 22;
      else if (incidentSize <= 1000) return 26;
      else if (incidentSize <= 5000) return 30;
      else if (incidentSize <= 50000) return 34;
      else return 38;
    }
    else {
      return 18;
    }
  }

  getRenderValueForFeature = (feature) => {
    const nonWildfire = (feature.attributes.IncidentTypeCategory !== 'RX' && feature.attributes.IncidentTypeCategory !== 'WF');
    const isComplete = (feature.attributes.StatusID === 1);
    const isCertified = (feature.attributes.StatusID === 3 || feature.attributes.ADSPermissionState === 'CERTIFIED');
    const nonWildFireIncomplete = (nonWildfire && !isComplete && !isCertified);
    const isQuarantined = (feature.attributes.IsQuarantined === 1);
    const prescribed = (feature.attributes.IncidentTypeCategory === 'RX');
    const notUnique = ((feature.attributes.MinUniqueness) && feature.attributes.MinUniqueness <= .25 && !isCertified);
    const notUniqueAndPrescribed = (prescribed && notUnique);


    if (isCertified) 
    {
      return 'Certified';
    }

    if (isQuarantined) 
    {
      return 'IsQuarantined';
    }

    if (notUnique) 
    {
     
      if (notUniqueAndPrescribed) 
      {
        return 'NonUniquePrescribed';
      }
      else 
      {
        return 'NonUnique';
      }
    }

    if (prescribed) 
    {
      return 'PrescribedFire';
    }

    if (nonWildFireIncomplete) 
    {
      return 'NonWildfire';
    }

    if (nonWildFireIncomplete) 
    {
      return 'NonWildfireComplete';
    }

    if (isComplete) 
    {
      return 'Complete';
    }

    return 'Incomplete';
  }

  //
  // PUBLIC METHODS
  //
  handleGetOriginDefinitionExpression = (done, failed) => {
    done(this.originFeatureLayer.definitionExpression);
  }

  handleSetCustomFilterDefinitionExpression = (where, done, failed) => {
    loadModules(['esri/geometry/Point', 'esri/geometry/SpatialReference'])
    .then(([Point, SpatialReference]) => {

      if (!where.includes(originFields.isValid)) 
      {
        where += " AND " + originFields.isValid + " = 1";
      }

      this.originFeatureLayer.definitionExpression = where;
      this.setSearchWidgetDefinition(where);
      if (this.originFeatureLayer) 
      {
        //this.originFeatureLayer.definitionExpression = expression;
        //TODO: Refactor this, duplicated in setOriginLayerDefinitionExpression
        const queryUrl = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
        window.api.getIncidentCountForQuery(this.view.extent, where, queryUrl).then(featureCount => {
          if (featureCount > 0) 
          {
            window.api.getIncidentsForQuery(this.view.extent, 0, [], where, queryUrl, this.maxCountForIncidents).then(features => 
              {
              
              this.fodrGraphicsLayer.graphics.removeAll();

              for (var i = 0; i < features.length; i++) {
                const currentFeature = features[i];
                var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
                symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);

                const g = new Graphic({
                  geometry: new Point({
                    x: currentFeature.geometry.x,
                    y: currentFeature.geometry.y,
                    spatialReference: new SpatialReference(3857)
                  }),
                  attributes: currentFeature.attributes,
                  symbol: symbol,
                  popupTemplate: this.originPopupTemplate
                }); 

                this.fodrGraphicsLayer.graphics.add(g);
              }
              
              done(
                {
                  total: featureCount, 
                  features: features
                });
            
            });
          } 
          else 
          {
            this.fodrGraphicsLayer.graphics.removeAll();
            
            done(
              {
                total: 0, 
                features: []
              });
          }
        });
      }
    });
  }

  handleGetFORIDsForQuery = (spatialFilterId, filterGeometry, resultOffset, results, whereClause, resolve, reject) => 
  {

    //Override filter geometry if we're passing in filterID
    const geometryToUse = (spatialFilterId !== null) ? this.spatialFilterPolygon : filterGeometry;
      const url = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
      window.api.GetForIDsForQuery(geometryToUse, resultOffset, results, whereClause, url).then(results => {
        resolve(results);
      });
  }
    
  //TODO: Refactor this entire thing.
  handleSetOriginDefinitionExpression = (filter, done, failed) => {
    this.shouldFilterOnPerimeters = (filter.perimeterMapMethod !== null || filter.perimeterFeatureStatus !== null || filter.perimeterFeatureAccess !== null);

    loadModules(['esri/geometry/Polygon'])
    .then(([Polygon]) => {

        if (this.originFeatureLayer) {
          let terms = [];
          //add individual where clauses to terms array
          //set clause to null if not applicable
          const fireCauseWhere = (filter.fireCauses.length > 0) ? `( ${originFields.fireCauseId} IN ('${filter.fireCauses.join("', '")}') )` : null;
          terms.push(fireCauseWhere);

          const utcOffsetWhere = (filter.utcOffset) ? `( UtcOffset =  ${filter.utcOffset} )` : null;
          terms.push(utcOffsetWhere);

          const dataSourcesWhere = (filter.dataSources.length > 0) ? `( ${originFields.dataSources} IN ('${filter.dataSources.join("', '")}') )` : null;
          terms.push(dataSourcesWhere);

          const multiRootWhere = (filter.multiRootOnly) ? `( ${originFields.isMultiRoot} = 1)` : null;
          terms.push(multiRootWhere);

          /*
          PrescribedEscapeRelationship
ComplexRelationship
MergeRelationship
PostFireRelationship
PotentialConflictRelationship
ProvidingResponseRelationship

1						ProvidingResponseTo
2						Complex
3						PotentialConflict
4						Merge
5						PrescribedEscape
6						PostFire

*/

          if (filter.irwinRelationships) 
          {
            let relationshipTerms = []; //validTerms.join(" AND ");
            if (filter.irwinRelationships.includes("1")) {
              relationshipTerms.push(" (ProvidingResponseRelationship = 1) ");
            }

            if (filter.irwinRelationships.includes("2")) {
              relationshipTerms.push(" (ComplexRelationship = 1) ");
            }

            if (filter.irwinRelationships.includes("3")) {
              relationshipTerms.push(" (PotentialConflictRelationship = 1) ");
            }

            if (filter.irwinRelationships.includes("4")) {
              relationshipTerms.push(" (MergeRelationship = 1) ");
            }

            if (filter.irwinRelationships.includes("5")) {
              relationshipTerms.push(" (PrescribedEscapeRelationship = 1) ");
            }

            if (filter.irwinRelationships.includes("6")) {
              relationshipTerms.push(" (PostFireRelationship = 1) ");
            }

            let relationshipWhere = "";
            if (relationshipTerms.length > 0) 
            {
              if (relationshipTerms.length === 1) {
                relationshipWhere = relationshipTerms[0];
              } else 
              {
                relationshipWhere = "( " + relationshipTerms.join(" OR ") + " ) ";
              }

              terms.push(relationshipWhere);
            }
          }

          const strategyWhere = (filter.strategies.length > 0) ? `( ${originFields.initialFireStrategy} IN ('${filter.strategies.join("', '")}') )` : null;
          terms.push(strategyWhere);

          const categoryWhere = (filter.categories.length > 0) ? `( ${originFields.incidentTypeCategory} IN ('${filter.categories.join("', '")}') )` : null;
          terms.push(categoryWhere);

          if (!categoryWhere) {
            const incidentTypeKindWhere = `(${originFields.incidentTypeKind} = 'FI' OR (${originFields.incidentTypeKind} = 'FM' AND (${originFields.incidentTypeCategory} = 'FA' OR ${originFields.incidentTypeCategory} = 'OR')))`;
            terms.push(incidentTypeKindWhere);
          }

          const landownerCategoryWhere = (filter.landownerCategories.length > 0) ? `( ${originFields.landownerCategory} IN ('${filter.landownerCategories.join("', '")}') )` : null;
          terms.push(landownerCategoryWhere);

          const acresMin = (filter.fireAcresMin === null) ? null : `( (${originFields.calculatedAcres} >= ${filter.fireAcresMin}) OR (${originFields.incidentSize} >= ${filter.fireAcresMin}) )`;
          terms.push(acresMin);

          if (filter.fireAcresMax !== null) {

            const dailyMax = `((${originFields.incidentSize} is NULL) OR (${originFields.incidentSize} <= ${filter.fireAcresMax}))`;
            const calculatedMax = `((${originFields.calculatedAcres} is NULL) OR (${originFields.calculatedAcres} <= ${filter.fireAcresMax}))`;
            let acresMax = `( ${dailyMax} AND ${calculatedMax} )`;

            terms.push(acresMax);
          }

          const maxDiscoveryDateString = (filter.discoveryDateMax) ? getSQLDateEndOfDay(filter.discoveryDateMax) : null;
          const maxDiscoveryDate = (maxDiscoveryDateString) ? `( ${originFields.discoveryDate} <= '${maxDiscoveryDateString}' )` : null;
          terms.push(maxDiscoveryDate);

          const minDiscoveryDateString = (filter.discoveryDateMin) ? getSQLDate(filter.discoveryDateMin) : null;
          const minDiscoveryDate = (minDiscoveryDateString) ? `( ${originFields.discoveryDate} >=  '${minDiscoveryDateString}' )` : null;
          terms.push(minDiscoveryDate);

          const maxOutDateString = (filter.outDateMax) ? getSQLDateEndOfDay(filter.outDateMax) : null;
          const maxOutDate = (maxOutDateString) ? `( ${originFields.outDate} <=  '${maxOutDateString}' )` : null;
          terms.push(maxOutDate);

          const minOutDateString = (filter.outDateMin) ? getSQLDate(filter.outDateMin) : null;
          const minOutDate = (minOutDateString) ? `( ${originFields.outDate} >=  '${minOutDateString}' )` : null;
          terms.push(minOutDate);

          const maxModifiedString = (filter.modifiedDateMax) ? getSQLDateEndOfDay(filter.modifiedDateMax) : null;
          const maxModifiedDate = (maxModifiedString) ? `( ${originFields.modifiedDate} <=  '${maxModifiedString}' )` : null;
          terms.push(maxModifiedDate);

          const minModifiedDateString = (filter.modifiedDateMin) ? getSQLDate(filter.modifiedDateMin) : null;
          const minModifiedDate = (minModifiedDateString) ? `( ${originFields.modifiedDate} >=  '${minModifiedDateString}' )` : null;
          terms.push(minModifiedDate);

          const gaccWhere = (filter.gacc && filter.gacc.length > 0) ? `( ${originFields.gacc} IN ('${filter.gacc.join("', '")}') )` : null;
          terms.push(gaccWhere);

          if (filter.state && filter.state.length > 0) {
            let stateWhere = "";
            if (filter.county === null || filter.county.length === 0) {
              stateWhere = `( ${originFields.state} IN ('${filter.state.join("', '")}') )`
            }
            else {
              stateWhere = (filter.county.length === 1)
                ? `(${originFields.state} = '${filter.state[0]}' AND ${originFields.county} = '${filter.county}')`
                : `(${originFields.state} = '${filter.state[0]}' AND ${originFields.county} IN ('${filter.county.join("', '")}'))`;
            }
            terms.push(stateWhere);
          }

          if (filter.structuresDestroyed !== null) {
            const structuresWhere = (filter.structuresDestroyed) ? `( ${originFields.residencesDestroyed} > 0 )` : `( ${originFields.residencesDestroyed} = 0  OR ${originFields.residencesDestroyed} IS NULL )`
            terms.push(structuresWhere);
          }

          if (filter.injuries !== null) {
            const injuriesWhere = (filter.injuries) ? `( ${originFields.injuries} > 0 )` : `( ${originFields.injuries} = 0  OR ${originFields.injuries} IS NULL )`
            terms.push(injuriesWhere);
          }
          //IMH
          if (filter.uniqueness !== null) {
            const uniquenessIndexWhere = `( MinUniqueness IS NOT NULL AND MinUniqueness <= ` + filter.uniqueness + `)`;
            terms.push(uniquenessIndexWhere);
          }

          if (filter.perimeterAcreageLandowner && filter.perimeterAcreageOperator && filter.perimeterAcreageNumber)  
          { 
            const acreageField = getAcresFieldName(filter.perimeterAcreageLandowner);
            const perimeterAcreageWhere = `( ${acreageField} ${filter.perimeterAcreageOperator} ${filter.perimeterAcreageNumber} )`;
            terms.push(perimeterAcreageWhere);  
          }

          if (filter.fatalities !== null) {
            const fatalitiesWhere = (filter.fatalities) ? `( ${originFields.fatalities} > 0 )` : `( ${originFields.fatalities} = 0  OR ${originFields.fatalities} IS NULL )`
            terms.push(fatalitiesWhere);
          }

          if (filter.otherStructuresDestroyed !== null) {
            const otherStructuresDestroyedWhere = (filter.otherStructuresDestroyed) ? `( ${originFields.structuresDestroyed} > 0 )` : `( ${originFields.structuresDestroyed} = 0  OR ${originFields.structuresDestroyed} IS NULL )`
            terms.push(otherStructuresDestroyedWhere);
          }

          if (filter.residencesThreatened !== null) {
            const residencesThreatenedWhere = (filter.residencesThreatened) ? `( ${originFields.residencesThreatened} > 0 )` : `( ${originFields.residencesThreatened} = 0  OR ${originFields.residencesThreatened} IS NULL )`
            terms.push(residencesThreatenedWhere);
          }

          if (filter.otherStructuresThreatened !== null) {
            const otherStructuresThreatenedWhere = (filter.otherStructuresThreatened) ? `( ${originFields.otherStructuresThreatened} > 0 )` : `( ${originFields.otherStructuresThreatened} = 0  OR ${originFields.otherStructuresThreatened} IS NULL )`
            terms.push(otherStructuresThreatenedWhere);
          }

          const modifiedBySystemWhere = (filter.modifiedBySystem && filter.modifiedBySystem.length > 0) ? `( ${originFields.modifiedBySystem} IN ('${filter.modifiedBySystem.join("', '")}') )` : null;
          terms.push(modifiedBySystemWhere);

          let createdBySystemWhere = (filter.createdBySystem && filter.createdBySystem.length > 0) ? `( ${originFields.createdBySystem} IN ('${filter.createdBySystem.join("', '")}') )` : null;
          if (!createdBySystemWhere) {
            createdBySystemWhere = `( ${originFields.createdBySystem} NOT IN ('irwinimp') )`;
          }

          terms.push(createdBySystemWhere);

          if (filter.certified !== null)  //TODO: Whatever this was, change it.
          {
            const isFederal = `(${originFields.incidentJurisdictionalAgency} IN ('${consts.federal_POOJurisdictionalAgency.join("', '")}'))`;
            const isNonFederal = `(${originFields.incidentJurisdictionalAgency} IS NULL OR ${originFields.incidentJurisdictionalAgency} NOT IN ('${consts.federal_POOJurisdictionalAgency.join("', '")}'))`;
            const federalHasRequired = `(${originFields.requiredFederal.join(" IS NOT NULL AND ")} IS NOT NULL )`;
            const nonFederalHasRequired = `(${originFields.requiredAllAgencies.join(" IS NOT NULL AND ")} IS NOT NULL )`;

            const federalComplete = `(${isFederal} AND ${federalHasRequired})`;
            const federalIncomplete = `(${isFederal} AND NOT ${federalHasRequired})`;

            const nonFederalComplete = `(${isNonFederal} AND ${nonFederalHasRequired})`;
            const nonFederalIncomplete = `(${isNonFederal} AND NOT ${nonFederalHasRequired})`;

            const certifiedWhere = `( ${originFields.certifiedField} = '${originFields.certifiedCodedValue}' AND IsValid = 1 AND ${originFields.isQuarantined} = 0)`;
            const completeWhere = `(  NOT ${certifiedWhere} AND (${federalComplete} OR ${nonFederalComplete}) AND IsValid = 1 AND ${originFields.isQuarantined} = 0)`;
            const incompleteWhere = `((${federalIncomplete} OR ${nonFederalIncomplete}) AND IsValid = 1 AND ${originFields.isQuarantined} = 0 AND ${originFields.certifiedField} <> '${originFields.certifiedCodedValue}')`;

            //const isQuarantinedOptionalWhere = `(${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`;
            const isQuarantinedOnlyWhere = `(${originFields.isQuarantined} = 1  AND IsValid = 1)`;
            const isNotQuarantinedOnlyWhere = `(${originFields.isQuarantined} = 0  AND IsValid = 1)`;

            var certified = filter.certified;
            let foundCert = false;

            let certifiedSubterms = [];

            if (certified.includes("ALL") || !certified) {
              const allStatusTypes = `(${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`;
              terms.push(allStatusTypes);
            }
            else {
              for (let i = 0; i < certified.length; i++) {
                if (certified[i] === consts.incidentStatus.certified) {
                  certifiedSubterms.push(certifiedWhere);
                  foundCert = true;
                }

                if (certified[i] === consts.incidentStatus.complete) {
                  certifiedSubterms.push(completeWhere);
                  foundCert = true;
                }

                if (certified[i] === consts.incidentStatus.incomplete) {
                  certifiedSubterms.push(incompleteWhere);
                  foundCert = true;
                }

                if (certified[i] === consts.incidentStatus.conflict) {
                  certifiedSubterms.push(isQuarantinedOnlyWhere);
                  foundCert = true;
                }

                if (certified[i] === 'IsNotQuarantined') {
                  certifiedSubterms.push(isNotQuarantinedOnlyWhere);
                  foundCert = true;
                }
              }

              if (!foundCert) {
                const allStatusTypes = `(${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`;
                terms.push(allStatusTypes);
              }
              else {
                if (certified.length === 1) {
                  terms.push("(" + certifiedSubterms[0] + ")  AND IsValid = 1");
                }
                else {
                  let certifiedSubClause = "(" + certifiedSubterms.join(" OR ") + " AND IsValid = 1)";
                  terms.push(certifiedSubClause);
                }
              }
            }
          }
          else {
            const allStatusTypes = `(${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`;
            terms.push(allStatusTypes);
          }

          if (filter.narrativeFilter) {
            const reportWhere = `( ${filter.narrativeFilter} )`;
            terms.push(reportWhere);
          }

          if (filter.incidentJurisdictionalAgency) {
            const incidentJurisdictionalAgencyWhere = `( UPPER(${originFields.incidentJurisdictionalAgency}) LIKE '%${filter.incidentJurisdictionalAgency.toUpperCase()}%' )`;
            terms.push(incidentJurisdictionalAgencyWhere);
          }

          if (filter.dispatchCenterID) {
            const dispatchCenterIDWhere = `( UPPER(${originFields.pooDispatchCenterID}) LIKE '${filter.dispatchCenterID.toUpperCase()}%' )`;
            terms.push(dispatchCenterIDWhere);
          }

          if (filter.jurisdictionalUnit) {
            const jurisdictionalUnitWhere = `( UPPER(${originFields.jurisdictionalUnit}) LIKE '%${filter.jurisdictionalUnit.toUpperCase()}%' )`;
            terms.push(jurisdictionalUnitWhere);
          }

          if (filter.pooProtectingUnit) {
            const pooProtectingUnitWhere = `( UPPER(${originFields.pooProtectingUnit}) LIKE '%${filter.pooProtectingUnit.toUpperCase()}%' )`;
            terms.push(pooProtectingUnitWhere);
          }

          if (filter.pooProtectingAgency) {
            const pooProtectingAgencyWhere = `( UPPER(${originFields.pooProtectingAgency}) LIKE '%${filter.pooProtectingAgency.toUpperCase()}%' )`;
            terms.push(pooProtectingAgencyWhere);
          }

          let overrideUniqueIdWhere = null;
          if (filter.uniqueFireIds && filter.uniqueFireIds.length > 0) {
            overrideUniqueIdWhere = `( ${originFields.forId} IN ('${filter.uniqueFireIds.join("', '")}') )`
          }

          
            const validTerms = terms.filter(cause => cause !== null);
            let where = (validTerms.length === 0) ? this.config.originLayer.defaultWhere : validTerms.join(" AND ");

            if (overrideUniqueIdWhere) 
            {
              where = (where) ? `(${where}) OR ${overrideUniqueIdWhere}` : null;
            }
            
            this.setSearchWidgetDefinition(where);
            let spatialGeometry;
            if (filter.geometry) 
            {
              const rings = (filter.geometry.rings) ? filter.geometry.rings : filter.geometry.geometry.rings;
              const poly = new Polygon({
                rings: rings,
                spatialReference: { wkid: 3857 }
              }); 

              this.handleSetSpatialFilterPolygon(poly);
              spatialGeometry = filter.geometry;
              this.queryForFeatures(where, spatialGeometry, done);
            } 
            else if (filter.hasSpatialFilter)
            {
              window.api.getSpatialFilterById(filter.spatialFilterID).then( response => {
                
                //this.props.setFilterGeometry(response.geometry);
                
                spatialGeometry = response.geometry;

                const poly = new Polygon({
                  rings: response.geometry.rings,
                  spatialReference: { wkid: 3857 }
                }); 

                this.handleSetSpatialFilterPolygon(poly);
                this.queryForFeatures(where, spatialGeometry, done);
              });
            } 
            else
            {
              this.spatialFilterGraphicsLayer.removeAll();
              var extents = this.view.extent.normalize();
              spatialGeometry = this.view.extent;
              if (extents.length > 1) 
              {
                this.queryForMultiExtentFeatures(where, extents[0], extents[1], done); 
              } 
              else  
             {
                this.queryForFeatures(where, extents[0], done);
            }
            }
        }
        else {
          failed('layer not found');
        }
      });
  }

  setSearchWidgetDefinition = (whereClause) => 
  {
    var mySource = this.searchWidget.sources.items[0];
    mySource.filter = { where: whereClause};
  } 

    queryForFeatures = (where, spatialGeometry, done) => {

      loadModules(['esri/Graphic', 'esri/geometry/Point', 'esri/geometry/SpatialReference'])
      .then(([Graphic, Point, SpatialReference]) => {

          this.originFeatureLayer.definitionExpression = where;

            const queryUrl = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
            window.api.getIncidentCountForQuery(spatialGeometry, where, queryUrl).then(featureCount => {
              if (featureCount > 0) 
              {
                window.api.getIncidentsForQuery(spatialGeometry, 0, [], where, queryUrl, this.maxCountForIncidents).then(features => 
                  {
                  
                  this.fodrGraphicsLayer.graphics.removeAll();

                  for (var i = 0; i < features.length; i++) {
                    const currentFeature = features[i];
                    var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
                    symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);

                    const g = new Graphic({
                      geometry: new Point({
                        x: currentFeature.geometry.x,
                        y: currentFeature.geometry.y,
                        spatialReference: new SpatialReference(3857)
                      }),
                      attributes: currentFeature.attributes,
                      symbol: symbol,
                      popupTemplate: this.originPopupTemplate
                    }); 
    
                    this.fodrGraphicsLayer.graphics.add(g);
                  } 
                  done(
                    {
                      total: featureCount, 
                      features: features
                    });
                 
                });
              } 
              else 
              {
                this.fodrGraphicsLayer.graphics.removeAll();
                
                done(
                  {
                    total: 0, 
                    features: []
                  });
              }
            });
          });
  }

  queryForMultiExtentFeatures = (where, extent1, extent2, done) => {

    loadModules(['esri/Graphic', 'esri/geometry/Point', 'esri/geometry/SpatialReference'])
    .then(([Graphic, Point, SpatialReference]) => {

          this.originFeatureLayer.definitionExpression = where;

          const queryUrl = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
          
          this.fodrGraphicsLayer.graphics.removeAll();

          let totalFeatureCount = 0;
          var totalFeatures = [];

          window.api.getIncidentCountForQuery(extent1, where, queryUrl).then(featureCount1 => {
            totalFeatureCount += featureCount1;
            window.api.getIncidentsForQuery(extent1, 0, [], where, queryUrl, this.maxCountForIncidents).then(features1 => 
            {
              for (var i = 0; i < features1.length; i++) {
                const currentFeature = features1[i];
                var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
                symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);

                const g = new Graphic({
                  geometry: new Point({
                    x: currentFeature.geometry.x,
                    y: currentFeature.geometry.y,
                    spatialReference: new SpatialReference(3857)
                  }),
                  attributes: currentFeature.attributes,
                  symbol: symbol,
                  popupTemplate: this.originPopupTemplate
                }); 
                
                totalFeatures.push(currentFeature);
                this.fodrGraphicsLayer.graphics.add(g);
              }
              
              window.api.getIncidentCountForQuery(extent2, where, queryUrl).then(featureCount2 => {
                  totalFeatureCount += featureCount2;
                  window.api.getIncidentsForQuery(extent2, 0, [], where, queryUrl, this.maxCountForIncidents).then(features2 => 
                    {
                    
                    //this.fodrGraphicsLayer.graphics.removeAll();
      
                    for (var i = 0; i < features2.length; i++) {
                      const currentFeature = features2[i];
                      var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
                      symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);
      
                      const g = new Graphic({
                        geometry: new Point({
                          x: currentFeature.geometry.x,
                          y: currentFeature.geometry.y,
                          spatialReference: new SpatialReference(3857)
                        }),
                        attributes: currentFeature.attributes,
                        symbol: symbol,
                        popupTemplate: this.originPopupTemplate
                      }); 
                      
                      totalFeatures.push(currentFeature);
                      this.fodrGraphicsLayer.graphics.add(g);
                    }
                                              
                  done(
                    {
                      total: totalFeatureCount, 
                      features: totalFeatures
                    });

                  });
                })
            });
      });
  });
}

  handleGetOriginFields = (done, failed) => {
    loadModules(['esri/layers/support/Field'])
      .then(([Field]) => {
        if (this.originFeatureLayer) {
          const fields = this.originFeatureLayer.fields.concat(
            [
              new Field({
                alias: "Latitude",
                name: "y",
                type: "double"
              }),
              new Field({
                alias: "Longitude",
                name: "x",
                type: "double"
              })
            ]
          );
          done(fields);
        }
        else {
          failed('Point of origin layer not found');
        }
      });
  }

  handleOriginWithinPerimeter = (perimeter, x, y, done, failed) => {
    loadModules(['esri/geometry/Polygon', 'esri/geometry/Point', 'esri/geometry/SpatialReference'])
      .then(([Polygon, Point, SpatialReference]) => {

        const incidentGeometry = new Point({
          x: x,
          y: y,
          spatialReference: new SpatialReference(4269)
        });

        if (perimeter === undefined || perimeter === null || incidentGeometry === undefined || incidentGeometry === null) 
        {
          done(false);
        }
        else {
          let perimeterPolygon = new Polygon(perimeter.geometry);
          const wasContained = perimeterPolygon.contains(incidentGeometry);
          done(wasContained);
        }
      });
  }

  handleOriginUpdateEnd = (cb, done, failed) => {

    if (this.originLayerView) {
      let handle = this.originLayerView.watch('updating', isUpdating => {
        if (isUpdating) {
          cb({ updating: true }, handle);
        }
        else {
          let query = this.originFeatureLayer.createQuery();
          query.returnGeometry = true;

          this.originLayerView.queryFeatures(query).then(
            results => {

              const filterResultsForPerimeter = (this.shouldFilterOnPerimeters && this.perimeterFilterIrwinIds && this.perimeterFilterIrwinIds.length > 0);

              const features = results ? results.features : [];
              const inViewGraphics = features.filter(graphic => this.view.extent.contains(graphic.geometry));
              const graphics = features.map(graphic => {
                return {
                  attributes: graphic.attributes,
                  geometry: graphic.geometry
                }
              });

              const visibleGraphics = inViewGraphics.map(graphic => {
                return {
                  attributes: graphic.attributes,
                  geometry: graphic.geometry
                }
              });

              cb({
                updating: false,
                graphics: (filterResultsForPerimeter) ? graphics.filter(graphic => this.perimeterFilterIrwinIds.includes(graphic.attributes[originFields.irwinId])) : graphics,
                visibleGraphics: (filterResultsForPerimeter) ? visibleGraphics.filter(graphic => this.perimeterFilterIrwinIds.includes(graphic.attributes[originFields.irwinId])) : visibleGraphics
              },
                handle
              )
            });
        }
      });


    }
    else {
      failed('Origin Layer View not found');
    }
  }


  handleGetOriginFeature = (id, done, failed) => {
    loadModules(['esri/rest/support/Query', 'esri/tasks/QueryTask', 'esri/geometry/support/webMercatorUtils'])
      .then(([Query, QueryTask, webMercatorUtils]) => {
        if (this.originLayerView) {

          let query = new Query({
            outFields: ['*'],
            where: `(${originFields.irwinId} = '${id}' AND ${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`,
            returnGeometry: true,
            outSpatialReference: this.view.spatialReference.toJSON()
          });

          const url = this.originApiItem.url + '/0';

          let queryTask = new QueryTask({ url: url });

          queryTask.execute(query).then(results => {
            let feature = null;

            if (results.features.length === 1) {

              const graphic = results.features[0];
              let geomWGS84 = webMercatorUtils.webMercatorToGeographic(graphic.geometry);
              feature = {
                attributes: Object.assign({}, graphic.attributes,
                  {
                    "_pointGeometryLatitude": geomWGS84.y,    //TODO: Can we just nuke _pointGeometryLatitude ?
                    "_pointGeometryLongitude": geomWGS84.x
                  }
                ),
                geometry: graphic.geometry.toJSON()
              };
            }
            done(feature);
          });
        }
      });
  }

  handleGetOriginFeatures = (geometry, done, failed) => {
    loadModules(['esri/rest/support/Query'])
      .then(([Query]) => {
        const query = (!geometry) ? null : new Query({
          geometry: geometry,
          spatialRelationship: "contains",
          returnGeometry: true
        });
        if (this.originLayerView) {
          const filterResultsForPerimeter = (this.shouldFilterOnPerimeters && this.perimeterFilterIrwinIds && this.perimeterFilterIrwinIds.length > 0);

          this.originLayerView.queryFeatures(query).then(
            results => {

              const featureToMapFrom = (filterResultsForPerimeter) ? results.features.filter(graphic => this.perimeterFilterIrwinIds.includes(graphic.attributes[originFields.irwinId])) : results.features;

              done({
                graphics: featureToMapFrom.map(graphic => {
                  return {
                    attributes: graphic.attributes,
                    geometry: graphic.geometry
                  }
                })
              })
            }
          );
        }
        else {
          failed('Origin Layer View null');
        }

      });
  }

  handleGetFullOriginFeaturesFromApi = async (ids, done, failed) => {

    loadModules(['esri/rest/support/Query', 'esri/tasks/QueryTask'])
      .then(([Query, QueryTask]) => {
        const query = new Query({
          where: `( ${originFields.irwinId} IN ('${ids.join("', '")}') AND ${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`,
          outFields: ['*'],
          returnGeometry: true
        });

        const url = this.originApiItem.url + '/0';
        const queryTask = new QueryTask({ url: url });

        queryTask.execute(query).then(results => {
          done({
            graphics: (!results.features) ? [] : results.features.map(graphic => {
              return {
                attributes: Object.assign({}, graphic.attributes,
                  {
                    "_pointGeometryLatitude": graphic.geometry.y,
                    "_pointGeometryLongitude": graphic.geometry.x
                  }
                ),
                geometry: null
              }
            })
          });

        });
      });
  }


  handleSetOriginSelectionGraphic = (point, done, failed) => {

    this.originSelectionGraphicsLayer.removeAll();

    this.originSelectionGraphicsLayer.add({
      geometry: point,
      symbol: this.originSelectionSymbol
    });

    done();
  }

  handleSetOriginSelectionGraphics = (args, done, failed) => {
    loadModules(['esri/geometry/Point'])
      .then(([Point]) => {

        if (args.clearExisting) 
        {
          this.originSelectionGraphicsLayer.removeAll();
        }
        
        if (args.graphics) 
        {
          args.graphics.map(graphic => {
            if (graphic && (graphic.geometry)) 
            {
              const point = new Point({
                x: graphic.geometry.x,
                y: graphic.geometry.y,
                spatialReference:  {
                  wkid: 3857
                }
              });

              return this.originSelectionGraphicsLayer.add({
                geometry: point,
                attributes: graphic.attributes,
                symbol: (args.useDisabledSymbol) ? this.originDisabledSymbol : this.originSelectionSymbol
              });
            }
            else 
            {
              return null;
            }

          });
        }

        done();
      });
  }

  handleClearOriginSelectionGraphics = (args, done, failed) => {
        if (args.clearExisting) {
          this.originSelectionGraphicsLayer.removeAll();
        }
        else if (args.graphics && args.graphics.length > 0) {
          const selectedUniqueIds = args.graphics.map(graphic => { return (graphic) ? graphic.attributes[originFields.forId] : null; });
          this.originSelectionGraphicsLayer.graphics.removeMany(
            this.originSelectionGraphicsLayer.graphics.filter(graphic => {
              return (graphic.attributes) ? selectedUniqueIds.includes(graphic.attributes[originFields.forId]) : true;
            })
          );
        }
        done();
  }

  handleSetOriginVisible = (visible, done, failed) => {
    if (!this.originFeatureLayer) {
      failed();
      return;
    }

    this.originFeatureLayer.visible = visible;
    this.fodrGraphicsLayer.visible = visible;
    done();
  }

  handleClearPerimeterGraphics = (done, failed) => {
    if (!this.perimeterGraphicsLayer) {
      failed();
      return;
    }

    this.perimeterGraphicsLayer.removeAll();
    done();
  }

  handleSetPerimeterGraphic = (perimeter, done, failed) => {
    loadModules(['esri/geometry/support/jsonUtils'])
      .then(([geometryJsonUtils]) => {

        if (!this.perimeterGraphicsLayer) {
          failed();
          return;
        }

        this.perimeterGraphicsLayer.removeAll();
        this.perimeterGraphicsLayer.add({
          geometry: geometryJsonUtils.fromJSON(perimeter.geometry),
          attributes: perimeter.attributes,
          symbol: symbols.selectedPerimeterSymbol
        });
        done();
      });
  }

  handleSetVisiblePerimeters = (args, done, failed) => {
    if (!this.perimeterFeatureLayer) {
      console.log("Could not set visible perimeters, perimeterFeatureLayer not found.")
      failed();
      return;
    }

    this.getCurrentPerimeterFeatures(args.id, args.useFinalService).then(
      results => {
        var targetPerimeterLayer = (args.useFinalService) ? this.finalPerimeterFeatureLayer : this.perimeterFeatureLayer;
        const oidField = targetPerimeterLayer.objectIdField;
        let features = [];
        const foundFeatures = (results.features && results.features.length > 0 && results.features[0] !== undefined);

        if (foundFeatures) {
          features = results.features;
          const oids = results.features.map(f => { return f.attributes[oidField]; });
          const oidWhere = ` ${targetPerimeterLayer.objectIdField} IN (${oids.join(", ")}) `;
          targetPerimeterLayer.visible = true;
          targetPerimeterLayer.definitionExpression = oidWhere;
        }
        else {
          //this.finalPerimeterFeatureLayer.definitionExpression = '';
          //this.finalPerimeterFeatureLayer.visible = false;
          //this.perimeterFeatureLayer.definitionExpression = '';
          //  this.perimeterFeatureLayer.visible = false;
        }
        done({ features: (args.returnFeatures) ? features : null })
      },
      error => {
        //this.finalPerimeterFeatureLayer.definitionExpression = '';
        //this.finalPerimeterFeatureLayer.visible = false;
        //  this.perimeterFeatureLayer.definitionExpression = '';
        //  this.perimeterFeatureLayer.visible = false;
        failed();
      }

    )
  }

  handleClearVisiblePerimeters = (args, done, failed) => {
    if (this.perimeterFeatureLayer) {
      //  this.perimeterFeatureLayer.visible = false;
      //    this.perimeterFeatureLayer.definitionExpression = "1=2";
      //    this.perimeterFeatureLayer.refresh();

      //this.finalPerimeterFeatureLayer.visible = false;
      //  this.finalPerimeterFeatureLayer.definitionExpression = "1=2";
      //this.finalPerimeterFeatureLayer.refresh();
    }
    done();
  }

  handleSetPerimetersSymbol = (args, done, failed) => {
    loadModules(['esri/renderers/SimpleRenderer'])
      .then(([SimpleRenderer]) => {
        if (this.perimeterFeatureLayer) {
          const renderer = new SimpleRenderer({ symbol: args.symbol });
          this.perimeterFeatureLayer.renderer = renderer;
          this.perimeterFeatureLayer.refresh();
        }

        if (this.finalPerimeterFeatureLayer) {
          const renderer = new SimpleRenderer({ symbol: args.symbol });
          this.finalPerimeterFeatureLayer.renderer = renderer;
          this.finalPerimeterFeatureLayer.refresh();
        }
        done();
      });
  }

  getCurrentPerimeterFeatures = (irwinId, useFinalService) => {
    return new Promise((resolve, reject) => {
      loadModules(['esri/rest/support/Query', 'esri/tasks/QueryTask'])
        .then(([Query, QueryTask]) => {

          resolve({ feature: [] });

          //TODO: Resolve Perimeter issues
          if (!this.perimeterFeatureLayer || (useFinalService && !this.finalPerimeterFeatureLayer)) {
            resolve({ feature: [] });
            return;
          }
          //remove guid braces { }
          let idStore = {};

          const formattedId = irwinId.replace(/\{|\}/g, '');
          idStore[irwinId] = { maxDate: -1 };

          let idWhere = `( ${uncertifiedPerimeterFields.irwinId} = '${formattedId}' OR ${uncertifiedPerimeterFields.irwinId} = '${irwinId}')`;
          if (!useFinalService) {
            idWhere += " AND (DeleteThis IS NULL OR DeleteThis = 'NO' OR DeleteThis = 'No')"
          }
          const perimeterTypes = uncertifiedPerimeterFields.perimeterTypeFeatureCategories;
          const featureCategoryWhereWhere = `${uncertifiedPerimeterFields.featureCategory} IN ('${perimeterTypes.join("', '")}')`

          //where: `${idWhere}  AND ${featureCategoryWhereWhere} AND SHAPE IS NOT NULL`,

          const query = new Query({
            where: `${idWhere} AND ${featureCategoryWhereWhere}`,
            outFields: ["*"],
            returnGeometry: true
          });
          const url = (useFinalService) ? (this.finalPerimeterFeatureLayer.url + '/' + this.finalPerimeterFeatureLayer.layerId) : (this.perimeterFeatureLayer.url + '/' + this.perimeterFeatureLayer.layerId);
          const perimetersToken = (useFinalService) ? this.finalPerimeterFeatureLayer.token : this.perimeterFeatureLayer.token;
          const queryTask = new QueryTask({ url: url });
          const dateField = uncertifiedPerimeterFields.mostCurrentDateField;

          queryTask.execute(query, { token: perimetersToken }).then(
            results => {
              if (results.features && results.features.length > 0) {
                for (let i = 0; i < results.features.length; i++) {
                  const feature = results.features[i];


                  if (feature.attributes["FeatureStatus"] === "proposed") {
                    feature.attributes["FeatureStatus"] = "Proposed";
                  }

                  let irwinId = feature.attributes[uncertifiedPerimeterFields.irwinId].toUpperCase();

                  let irwinKey = (irwinId.slice(-1) !== '}' && irwinId.charAt(0) !== '{') ? '{' + irwinId + '}' : irwinId;

                  const date = new Date(feature.attributes[dateField]).getTime();
                  if (date > idStore[irwinKey].maxDate && (feature.geometry) && feature.geometry.rings.length > 0) {
                    idStore[irwinKey].maxDate = date;
                    idStore[irwinKey].feature = {

                      attributes: Object.assign({}, feature.attributes,
                        {
                          "PerimeterSource": (useFinalService) ? uncertifiedPerimeterFields.finalPerimeterSource : uncertifiedPerimeterFields.nifsPerimeterSource
                        }),
                      geometry: feature.geometry.toJSON()
                    };
                  }
                }

                let features = []
                for (var prop in idStore) {
                  // skip loop if the property is from prototype
                  if (idStore.hasOwnProperty(prop)) {
                    const item = idStore[prop];
                    features.push(item.feature)
                  }

                }
                resolve({ features: features });
              }
              else {
                //IMH: No features found. Try again with NIFS if we were checking final, or resolve with nothing.
                if (useFinalService) {
                  return this.getCurrentPerimeterFeatures(irwinId, false);
                }
                else {
                  resolve({ feature: [] });
                }
              }
            },
            error => {
              reject();
            }
          );
        });
    });
  }

  getPerimeterFeaturesForIrwinIds = async (irwinIds) => {
    return new Promise((resolve, reject) => {
      loadModules(['esri/rest/support/Query', 'esri/tasks/QueryTask'])
        .then(([Query, QueryTask]) => {
          if (!this.perimeterFeatureLayer) {
            resolve({ feature: [] });
            return;
          }
          let idClauses = [];
          let idStore = {};

          for (var i = 0; i < irwinIds.length; i++) {
            idClauses.push("'" + irwinIds[i].replace(/\{|\}/g, '') + "'");
            idClauses.push("'" + irwinIds[i] + "'");
            idStore[irwinIds[i]] = { maxDate: -1 };
            if (i === 1999) { break; }   //Geoplatform has a 2000 record limit
          }
          //remove guid braces { }
          //const formattedId = irwinId.replace(/\{|\}/g,'');

          const idWhere = `${uncertifiedPerimeterFields.irwinId} IN (${idClauses.join(",")})`;

          const query = new Query({
            where: `${idWhere}`,
            outFields: ["*"],
            returnGeometry: false
          });
          const url = (this.perimeterFeatureLayer.url + '/' + this.perimeterFeatureLayer.layerId);
          const queryTask = new QueryTask({ url: url });
          const dateField = uncertifiedPerimeterFields.mostCurrentDateField;

          queryTask.execute(query, { token: this.perimeterFeatureLayer.token }).then(
            results => {
              if (results.features && results.features.length > 0) {
                for (let i = 0; i < results.features.length; i++) {
                  const feature = results.features[i];
                  let irwinId = feature.attributes[uncertifiedPerimeterFields.irwinId].toUpperCase();

                  let irwinKey = (irwinId.slice(-1) !== '}' && irwinId.charAt(0) !== '{') ? '{' + irwinId + '}' : irwinId;

                  const date = new Date(feature.attributes[dateField]).getTime();
                  if (idStore[irwinKey] !== undefined && date > idStore[irwinKey].maxDate) {
                    idStore[irwinKey].maxDate = date;
                    idStore[irwinKey].feature = {

                      attributes: Object.assign({}, feature.attributes,
                        {
                          "PerimeterSource": uncertifiedPerimeterFields.nifsPerimeterSource
                        })
                    };
                  }
                }

                let features = []
                for (var prop in idStore) {
                  // skip loop if the property is from prototype
                  if (idStore.hasOwnProperty(prop)) {
                    const item = idStore[prop];
                    features.push(item.feature)
                  }

                }
                resolve({ features: features });
              } else {
                resolve({ features: [] });
              }
            },
            error => {
              reject();
            }
          );
        });
    });
  }

  handleSetModifiedPerimeter = (args, done, failed) => {
    loadModules(['esri/geometry/support/jsonUtils'])
      .then(([geometryJsonUtils]) => {
        this.perimeterModifiedGraphicsLayer.removeAll();
        if (args.graphics) {
          args.graphics.map(graphic => {
            if (graphic) {
              return this.perimeterModifiedGraphicsLayer.add({
                geometry: geometryJsonUtils.fromJSON(graphic.geometry),
                attributes: graphic.attributes,
                symbol: symbols.selectedPerimeterSymbol
              });
            }
            else {
              return null;
            }

          });
        }

        done();
      });
  }

  handleAddDebugGraphic = (graphic, attributes, isConflict, done, failed) => {
    loadModules(['esri/geometry/support/jsonUtils'])
      .then(([geometryJsonUtils]) => {
        return this.debugGraphicsLayer.add({
          geometry: geometryJsonUtils.fromJSON(graphic),
          attributes: attributes,
          symbol: (isConflict) ? symbols.debugIncidentGraphic : symbols.debugBlueIncidentGraphic
        });
      });
  }

  handleSetModifiedPerimeterNoClear = (args, done, failed) => {
    loadModules(['esri/geometry/support/jsonUtils'])
      .then(([geometryJsonUtils]) => {
        if (args.graphics) {
          args.graphics.map(graphic => {
            if (graphic) {
              return this.perimeterModifiedGraphicsLayer.add({
                geometry: geometryJsonUtils.fromJSON(graphic.geometry),
                attributes: graphic.attributes,
                symbol: symbols.selectedPerimeterSymbol
              });
            }
            else {
              return null;
            }

          });
        }

        done();
      });
  }

  handleClearModifiedPerimeter = (args, done, failed) => {
    this.perimeterModifiedGraphicsLayer.removeAll();
    done();
  }

  setSpatialFilterGraphic = (geometryJSON) => {
    loadModules([
      'esri/geometry/geometryEngine',
      'esri/geometry/support/jsonUtils',
      'esri/geometry/support/webMercatorUtils'
    ])
      .then(([GeometryEngine, geometryJsonUtils, WebMercatorUtils]) => {

        if (geometryJSON === null) 
        {
          return;
        }

        this.spatialFilterGraphicsLayer.removeAll();
        let geometry = geometryJsonUtils.fromJSON(geometryJSON)
        if (geometry.spatialReference.isGeographic) 
        {
          geometry = WebMercatorUtils.geographicToWebMercator(geometry);
        }

        var maskGeometry = GeometryEngine.difference(this.universeWebMercator, geometry);

        this.spatialFilterGraphicsLayer.add({
          geometry: maskGeometry,
          symbol: symbols.maskSymbol
        })
      });
  }

  handleSetSpatialFilterPolygon = (polygon) => {
    loadModules([
      'esri/geometry/geometryEngine',
    ])
      .then(([GeometryEngine]) => {

        this.spatialFilterPolygon = polygon;
        this.spatialFilterGraphicsLayer.removeAll();
         if (polygon === null) 
         {
          return;
         }
         
        this.spatialFilterGraphicsLayer.add({
          geometry: GeometryEngine.difference(this.universeWebMercator, polygon),
          symbol: symbols.maskSymbol
        })

      });
  }

  handleOriginRefresh = (done, failed) => {

    if (this.view.type === '2d') 
    {
      this.originFeatureLayer.refresh();
    }
    else 
    {
      if (this.originFeatureLayer.definitionExpression) 
      {
        this.originFeatureLayer.definitionExpression += " AND 1=1";
      }
      else {
        this.originFeatureLayer.definitionExpression = "1=1";
      }
    }
    done();

  }

  getOriginPopupContent = (target) => {
    return new Promise((resolve, reject) => {

      loadModules(['esri/rest/support/Query', 'esri/tasks/QueryTask'])
        .then(([Query, QueryTask]) => {
          const query = new Query({
            where: `OBJECTID  = '${target.graphic.attributes["OBJECTID"]}'`,
            outFields: ["*"],
            returnGeometry: false
          });
          const url = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId;
          const queryTask = new QueryTask({ url: url });

          queryTask.execute(query).then(
            results => {
              let content;
              if (results && results.features && results.features.length === 1) {
                const attrs = results.features[0].attributes;
                let acres = attrs[originFields.incidentSize];
                const dateFormat = "MM/DD/YYYY";

                if (acres) {
                  acres = acres.toFixed(2)
                }

                content = '' +
                  '<div class="popup-incident-primary-fields">' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Incident Name</label>' +
                  '<p class="list-item-incident-name">' + (attrs[originFields.name] || '––') + '</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>ID</label>' +
                  '<p class="list-item-detail-label">' + (attrs[originFields.uniqueFireId] || '––') + '</p>' +
                  '</div>' +
                  '</div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Fire Size</label>' +
                  '<p>' + (acres || '––') + '</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>FireCode</label>' +
                  '<p>' + (attrs[originFields.fireCode] || '––') + '</p>' +
                  '</div>' +
                  '</div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Discovery</label>' +
                  '<p>' + (attrs[originFields.discoveryDate] ? moment(attrs[originFields.discoveryDate]).format(dateFormat) : '––') + '</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>Fire Out</label>' +
                  '<p>' + (attrs[originFields.outDate] ? moment(attrs[originFields.outDate]).format(dateFormat) : "––") + '</p>' +
                  '</div>' +
                  '</div>' +
                  '</div>';
              }
              resolve(content);
            },
            error => {
              reject();
            }
          );

        });

    });

  }


  getPerimeterPopup = (target) => {
    return new Promise((resolve, reject) => {

      loadModules(['esri/rest/support/Query', 'esri/tasks/QueryTask'])
        .then(([Query, QueryTask]) => {
          const query = new Query({
            where: `${originFields.irwinId}  = '${target.graphic.attributes[originFields.irwinId]}'`,
            outFields: ["*"],
            returnGeometry: false
          });

          const url = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId;
          const queryTask = new QueryTask({ url: url });

          queryTask.execute(query).then(
            results => {
              let content;
              if (results && results.features && results.features.length === 1) {
                const attrs = results.features[0].attributes;
                let acres = attrs[originFields.incidentSize];
                const dateFormat = "MM/DD/YYYY";

                if (acres) {
                  acres = acres.toFixed(2)
                }

                content = '' +
                  '<div class="popup-incident-primary-fields">' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Incident Name</label>' +
                  '<p class="list-item-incident-name">' + (attrs[originFields.name] || '––') + '</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>ID</label>' +
                  '<p class="list-item-detail-label">' + (attrs[originFields.uniqueFireId] || '––') + '</p>' +
                  '</div>' +
                  '</div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Fire Size</label>' +
                  '<p>' + (acres || '––') + '</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>FireCode</label>' +
                  '<p>' + (attrs[originFields.fireCode] || '––') + '</p>' +
                  '</div>' +
                  '</div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Discovery</label>' +
                  '<p>' + (attrs[originFields.discoveryDate] ? moment(attrs[originFields.discoveryDate]).format(dateFormat) : '––') + '</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>Fire Out</label>' +
                  '<p>' + (attrs[originFields.outDate] ? moment(attrs[originFields.outDate]).format(dateFormat) : "––") + '</p>' +
                  '</div>' +
                  '</div>' +
                  '</div>';
              }
              resolve(content);
            },
            error => {
              reject();
            }
          );

        });

    });

  }
}
